import type { FC } from "react";
import { useStore } from "@tanstack/react-form";

import {
  FieldDatepicker,
  FieldInput,
  FieldRadio,
  FieldSelect,
} from "@/components/form";
import FormComponentTitle from "@/components/form/form-component-title";
import type { UpdateFieldProps } from "@/components/form/patient/update-form";
import { Separator } from "@/components/ui/separator";
import { updatePatientSchema } from "@/schemas/patient";
import { noYesRadioOptions, yesNoRadioOptions } from "@/utils/form";
import {
  diabeticOptions,
  smokingOptions,
  stressedOptions,
  toothBrushHairTypeOptions,
  toothBrushTypeOptions,
} from "@/utils/patient";

const RiskFactorsForm: FC<UpdateFieldProps> = ({ formApi }) => {
  const smokingValue = useStore(formApi.store, (state) => state.values.smoking);

  return (
    <div className="flex flex-col gap-4 w-full risk-factors-container">
      <FormComponentTitle
        title="Facteurs de risque"
        subTitle="Ajouter les informations du patient"
      />
      <Separator />
      <div className="flex gap-8">
        <label className="text-sm font-semibold text-gray-700 lg:w-full lg:max-w-[280px] flex-shrink-0">
          Brossage
        </label>
        <div className="flex flex-col gap-4 w-full lg:max-w-[512px]">
          <div className="flex flex-col sm:flex-row gap-4">
            <FieldSelect
              Field={formApi.Field}
              schema={updatePatientSchema}
              name="toothBrush"
              label="Type de brosse à dents"
              options={toothBrushTypeOptions}
              validators={{
                onChange: updatePatientSchema.shape.toothBrush,
              }}
            />
            <FieldSelect
              Field={formApi.Field}
              schema={updatePatientSchema}
              name="toothBrushHair"
              label="Poils de brosse à dents"
              options={toothBrushHairTypeOptions}
              validators={{
                onChange: updatePatientSchema.shape.toothBrushHair,
              }}
            />
            <FieldInput
              Field={formApi.Field}
              schema={updatePatientSchema}
              name="brushingFrequency"
              placeholder="0"
              label="Fréquence"
              validators={{
                onChange: updatePatientSchema.shape.brushingFrequency,
              }}
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <FieldRadio
              Field={formApi.Field}
              schema={updatePatientSchema}
              name="interdentalSystem"
              label="Système interdentaire"
              options={yesNoRadioOptions}
              type="boolean"
              orientation="horizontal"
              validators={{
                onChange: updatePatientSchema.shape.interdentalSystem,
              }}
            />
            <FieldInput
              Field={formApi.Field}
              schema={updatePatientSchema}
              name="brushingAccessories"
              placeholder="Ajouter des accessoires"
              label="Accessoires de brossage"
              validators={{
                onChange: updatePatientSchema.shape.brushingAccessories,
              }}
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <FieldInput
              Field={formApi.Field}
              schema={updatePatientSchema}
              name="toothPaste"
              placeholder="Dentifrice"
              label="Dentifrice"
              validators={{
                onChange: updatePatientSchema.shape.toothPaste,
              }}
            />
            <FieldInput
              Field={formApi.Field}
              schema={updatePatientSchema}
              name="mouthWash"
              placeholder="Bain de bouche"
              label="Bain de bouche"
              validators={{
                onChange: updatePatientSchema.shape.mouthWash,
              }}
            />
          </div>
        </div>
      </div>
      <Separator />
      <div className="flex gap-8">
        <label className="text-sm font-semibold text-gray-700 lg:w-full lg:max-w-[280px] flex-shrink-0">
          Tabac
        </label>
        <div className="flex flex-col gap-4 w-full lg:max-w-[512px]">
          <FieldRadio
            Field={formApi.Field}
            schema={updatePatientSchema}
            name="smoking"
            label="Patient fumeur"
            options={smokingOptions}
            orientation="horizontal"
            validators={{
              onChange: updatePatientSchema.shape.smoking,
            }}
          />
          {smokingValue === "TRUE" ? (
            <FieldInput
              Field={formApi.Field}
              schema={updatePatientSchema}
              name="dailySmokes"
              placeholder="0"
              label="Nombre de cigarettes par jour"
              type="number"
              min={0}
              validators={{
                onChange: updatePatientSchema.shape.dailySmokes,
              }}
            />
          ) : null}
          {smokingValue === "PREVIOUS" ? (
            <div className="flex gap-4">
              <FieldDatepicker
                Field={formApi.Field}
                name={"smokingEnd"}
                label="Date d'arrêt"
                schema={updatePatientSchema}
                validators={{ onChange: updatePatientSchema.shape.smokingEnd }}
              />
              <FieldInput
                Field={formApi.Field}
                schema={updatePatientSchema}
                name="dailySmokes"
                placeholder="0"
                label="Nombre de cigarettes par jour"
                type="number"
                min={0}
                validators={{
                  onChange: updatePatientSchema.shape.dailySmokes,
                }}
              />
            </div>
          ) : null}
        </div>
      </div>
      <Separator />
      <div className="flex gap-8">
        <label className="text-sm font-semibold text-gray-700 lg:w-full lg:max-w-[280px] flex-shrink-0">
          Diabète
        </label>
        <div className="flex flex-col gap-4 w-full lg:max-w-[512px]">
          <FieldRadio
            Field={formApi.Field}
            schema={updatePatientSchema}
            name="diabetic"
            label="Patient diabétique"
            options={diabeticOptions}
            orientation="horizontal"
            validators={{
              onChange: updatePatientSchema.shape.diabetic,
            }}
          />
          <FieldInput
            Field={formApi.Field}
            schema={updatePatientSchema}
            name="hba1c"
            placeholder="4.5"
            label="HbA1c"
            type="number"
            min={0}
            max={100}
            isPercentage={true}
            validators={{
              onChange: updatePatientSchema.shape.hba1c,
            }}
          />
          <FieldInput
            Field={formApi.Field}
            schema={updatePatientSchema}
            name="diabetComment"
            placeholder="Ajouter un commentaire"
            label="Commentaire"
            validators={{
              onChange: updatePatientSchema.shape.diabetComment,
            }}
          />
        </div>
      </div>
      <Separator />
      <div className="flex gap-8">
        <label className="text-sm font-semibold text-gray-700 lg:w-full lg:max-w-[280px] flex-shrink-0">
          Prédisposition
        </label>
        <div className="flex flex-col gap-4 w-full lg:max-w-[512px]">
          <FieldRadio
            Field={formApi.Field}
            schema={updatePatientSchema}
            name="geneticTendency"
            label="Patient prédisposé génétiquement"
            options={noYesRadioOptions}
            orientation="horizontal"
            type="boolean"
            validators={{
              onChange: updatePatientSchema.shape.geneticTendency,
            }}
          />
          <FieldInput
            Field={formApi.Field}
            schema={updatePatientSchema}
            name="geneticComment"
            placeholder="Ajouter un commentaire"
            label="Commentaire"
            validators={{
              onChange: updatePatientSchema.shape.geneticComment,
            }}
          />
        </div>
      </div>
      <Separator />
      <div className="flex gap-8">
        <label className="text-sm font-semibold text-gray-700 lg:w-full lg:max-w-[280px] flex-shrink-0">
          Stress
        </label>
        <div className="flex flex-col gap-4 w-full max-w-[512px]">
          <FieldSelect
            Field={formApi.Field}
            schema={updatePatientSchema}
            name="stress"
            label="Sujet au stress"
            options={stressedOptions.map((option) => ({
              value: option.value.toString(),
              label: option.label,
            }))}
            type="number"
            validators={{
              onChange: updatePatientSchema.shape.stress,
            }}
          />
          <FieldInput
            Field={formApi.Field}
            schema={updatePatientSchema}
            name="stressComment"
            placeholder="Ajouter un commentaire"
            label="Commentaire"
            validators={{
              onChange: updatePatientSchema.shape.stressComment,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default RiskFactorsForm;
